import {
  FETCH_CONVERSATIONS_REQUEST,
  FETCH_CONVERSATIONS_SUCCESS,
  FETCH_CONVERSATIONS_FAILURE,
  SET_SELECTED_CONVERSATION,
  SET_CONVERSATIONS_CURRENT_PAGE,
  SET_ACTIVE_INBOX_FILTER,
  SET_ACTIVE_INBOX_SORT,
  SET_EXTERNAL_CONVERSATION
} from '../../actions/inbox/conversationActions';
import {
  UPDATE_OR_ADD_CONVERSATION
} from '../../actions/inbox/conversationOptimizedActions';
import { conversationMatchesFilter } from '../../services/inbox/conversationFilterEvaluator';
import { toSidebarPlatformKey } from '../../services/inbox/inboxMsChatPlatform';

function hasActiveActivityQuickFilter(activeFilter) {
  const activity = activeFilter?.quickFilters?.activity;
  return Array.isArray(activity) && activity.length > 0;
}

function getConversationInteractionTimestamp(conversation) {
  const attrs = conversation?.attributes || conversation;
  const dateStr = attrs.interaction_date || attrs.recent_message_date || 0;
  const timestamp = new Date(dateStr).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function shouldMoveConversationToTop(existingConversation, changes) {
  if (!changes || typeof changes !== 'object') return false;

  const dateFields = ['interaction_date', 'recent_message_date'];
  const touchesInteractionDate = dateFields.some(
    (field) => Object.prototype.hasOwnProperty.call(changes, field)
  );
  if (!touchesInteractionDate) return false;

  const previousTimestamp = getConversationInteractionTimestamp(existingConversation);
  const nextAttributes = {
    ...(existingConversation?.attributes || {}),
    ...changes
  };
  const nextTimestamp = getConversationInteractionTimestamp({ attributes: nextAttributes });

  return nextTimestamp > previousTimestamp;
}

function normalizeConversations(conversations = []) {
  const seenConversationIds = new Set();
  const normalizedData = [];
  const conversationIndexMap = {};

  conversations.forEach((conversation) => {
    const conversationId = conversation?.id;
    if (conversationId == null) return;

    const conversationKey = String(conversationId);
    if (seenConversationIds.has(conversationKey)) return;

    seenConversationIds.add(conversationKey);
    conversationIndexMap[conversationKey] = normalizedData.length;
    normalizedData.push(conversation);
  });

  return { normalizedData, conversationIndexMap };
}

const initialState = {
  loading: false,
  data: [],
  conversationIndexMap: {},
  pagination: {
    current_page: 1
  },
  error: null,
  selectedConversationId: null,
  activeFilter: null,
  activeSortConfig: null,
  // Conversación abierta desde fuera de la lista (otro canal del mismo customer).
  // Ver setExternalConversation y useSelectedConversation.
  externalConversation: null
};

/**
 * Aplica `changes` sobre el slot externo si corresponde al id indicado.
 * Devuelve el slot sin tocar cuando no aplica, para no romper la igualdad
 * referencial del estado.
 */
function mergeExternalConversation(externalConversation, key, changes) {
  if (!externalConversation || String(externalConversation.id) !== key) {
    return externalConversation;
  }

  return {
    ...externalConversation,
    attributes: {
      ...externalConversation.attributes,
      ...changes
    }
  };
}

export default function conversationsReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_CONVERSATIONS_REQUEST: {
      const isFirstPage = action.meta?.page === 1;
      if (!isFirstPage) {
        return { ...state, loading: true, error: null };
      }
      return {
        ...state,
        loading: true,
        error: null,
        data: [],
        conversationIndexMap: {},
        pagination: {
          ...state.pagination,
          current_page: 1,
          mercatelyOffset: 0,
          inboxMsOffset: 0,
          total_count: 0,
          total_pages: 0,
          has_next_page: false,
          has_previous_page: false
        }
      };
    }
    case FETCH_CONVERSATIONS_SUCCESS: {
      const newData = action.payload?.data || [];
      const currentPage = action.payload?.pagination?.current_page || 1;

      const mergedData = currentPage > 1
        ? [...state.data, ...newData]
        : newData;
      const { normalizedData, conversationIndexMap } = normalizeConversations(mergedData);

      return {
        ...state,
        loading: false,
        data: normalizedData,
        conversationIndexMap,
        pagination: {
          ...state.pagination,
          ...action.payload?.pagination
        },
        error: null
      };
    }
    case FETCH_CONVERSATIONS_FAILURE:
      return {
        ...state,
        loading: false,
        data: [],
        conversationIndexMap: {},
        pagination: {
          current_page: 1
        },
        error: action.error
      };
    case SET_SELECTED_CONVERSATION: {
      const rawPayload = action.payload;
      const nextId = rawPayload && typeof rawPayload === 'object' && 'conversationId' in rawPayload
        ? rawPayload.conversationId
        : rawPayload;
      const currentUserIdForFilter = rawPayload && typeof rawPayload === 'object' && 'currentUserId' in rawPayload
        ? rawPayload.currentUserId
        : undefined;
      const currentRetailerUserForFilter = rawPayload && typeof rawPayload === 'object' && 'currentRetailerUser' in rawPayload
        ? rawPayload.currentRetailerUser
        : undefined;

      const activityFiltersActive = hasActiveActivityQuickFilter(state.activeFilter);
      const previousId = state.selectedConversationId;
      const previousKey = previousId != null ? String(previousId) : null;
      const nextKey = nextId != null ? String(nextId) : null;

      let { data, conversationIndexMap } = state;

      if (activityFiltersActive && previousKey && previousKey !== nextKey) {
        const prevIndex = state.conversationIndexMap[previousKey];
        if (prevIndex !== undefined) {
          const previousConversation = state.data[prevIndex];
          const stillMatches = conversationMatchesFilter(
            previousConversation,
            state.activeFilter,
            currentUserIdForFilter,
            { currentRetailerUser: currentRetailerUserForFilter }
          );
          if (!stillMatches) {
            data = [...state.data];
            data.splice(prevIndex, 1);
            const normalizedResult = normalizeConversations(data);
            data = normalizedResult.normalizedData;
            conversationIndexMap = normalizedResult.conversationIndexMap;
          }
        }
      }

      // Al volver a un chat que sí está en la lista (o al deseleccionar), el slot
      // externo deja de hacer falta y no debe quedar colgado.
      const nextIsInList = nextKey != null && conversationIndexMap[nextKey] !== undefined;
      const externalConversation = (nextKey == null || nextIsInList)
        ? null
        : state.externalConversation;

      return {
        ...state,
        selectedConversationId: nextId,
        externalConversation,
        ...(data !== state.data ? { data, conversationIndexMap } : {})
      };
    }
    case SET_EXTERNAL_CONVERSATION:
      return {
        ...state,
        externalConversation: action.payload || null
      };
    case UPDATE_OR_ADD_CONVERSATION: {
      const {
        id,
        changes,
        isUpsert = false,
        currentUserId,
        currentRetailerUser,
        conversationListType
      } = action.payload;

      if (!id || !changes) return state;

      const key = String(id);
      // El chat abierto desde otro canal no vive en `data` y normalmente no cumple
      // el filtro activo, así que hay que actualizarlo aparte: sin esto las acciones
      // del header (estado, agente, bot, no leído) no se reflejan en la UI.
      const externalConversation = mergeExternalConversation(
        state.externalConversation,
        key,
        changes
      );
      const {
        normalizedData: deduplicatedData,
        conversationIndexMap: deduplicatedMap
      } = normalizeConversations(state.data);
      const newData = [...deduplicatedData];
      const existingIndex = deduplicatedMap[key];

      // El chat externo no es miembro de la lista: si no está en `data`, no se inserta.
      // Insertarlo crearía una entrada cuyos `attributes` son solo los `changes` —sin
      // `platform`—, y como `useSelectedConversation` prefiere `data` sobre el slot, el
      // header pasaría a leer ese stub y todas las escrituras se irían al monolito por
      // `isInboxMsChat(undefined) === false`.
      if (existingIndex === undefined && externalConversation !== state.externalConversation) {
        return { ...state, externalConversation };
      }

      // La plataforma de un chat ya conocido no se degrada a la clave de canal.
      // `whatsapp` agrupa tres enums del MS y no resuelve a ninguno, así que sobrescribir
      // `whatsapp_gupshup_messages` con ella convierte el chat en "nativo del monolito" y
      // manda mensajes y marcado de leído a Rails. Vale para cualquier productor que colapse
      // la clave —hoy el socket, mañana otro— y cubre la ventana de deploy en la que el
      // notifications-service viejo sigue emitiendo la corta.
      const existingPlatform = existingIndex !== undefined
        ? newData[existingIndex].attributes?.platform
        : undefined;
      const downgradesPlatform = existingPlatform
        && changes?.platform
        && changes.platform !== existingPlatform
        && toSidebarPlatformKey(existingPlatform) === changes.platform;
      const safeChanges = downgradesPlatform
        ? { ...changes, platform: existingPlatform }
        : changes;

      const resolvedListType = (downgradesPlatform ? existingPlatform : conversationListType)
        || (existingIndex !== undefined ? newData[existingIndex].type : null)
        || safeChanges?.platform;

      // Construir conversación actualizada
      let updatedConversation;

      if (existingIndex !== undefined) {
        updatedConversation = {
          ...newData[existingIndex],
          type: resolvedListType,
          attributes: {
            ...newData[existingIndex].attributes,
            ...safeChanges
          }
        };
      } else {
        updatedConversation = {
          id: key,
          type: resolvedListType,
          attributes: { ...safeChanges }
        };
      }

      // Evaluar filtro activo
      const shouldKeep = conversationMatchesFilter(
        updatedConversation,
        state.activeFilter,
        currentUserId,
        { currentRetailerUser }
      );

      const activityFiltersActive = hasActiveActivityQuickFilter(state.activeFilter);
      const isSelectedChat = state.selectedConversationId != null
        && key === String(state.selectedConversationId);

      // Si NO cumple filtro
      if (!shouldKeep) {
        // Con filtros de actividad, no quitar de la lista la conversación ya abierta
        // (p. ej. al leer o responder deja de cumplir `no_read` / `no_answer`).
        if (activityFiltersActive && isSelectedChat && existingIndex !== undefined) {
          newData[existingIndex] = updatedConversation;
          const { normalizedData, conversationIndexMap } = normalizeConversations(newData);
          return {
            ...state,
            data: normalizedData,
            conversationIndexMap,
            externalConversation
          };
        }

        // Si existe → eliminar
        if (existingIndex !== undefined) {
          newData.splice(existingIndex, 1);
          const { normalizedData, conversationIndexMap } = normalizeConversations(newData);

          return {
            ...state,
            data: normalizedData,
            conversationIndexMap,
            externalConversation
          };
        }

        // Si no existe → solo queda el slot externo por actualizar
        if (externalConversation === state.externalConversation) return state;
        return { ...state, externalConversation };
      }

      // Si cumple filtro

      if (existingIndex !== undefined) {
        const shouldReorder = isUpsert
          && shouldMoveConversationToTop(newData[existingIndex], changes);

        if (shouldReorder) {
          newData.splice(existingIndex, 1);
          newData.unshift(updatedConversation);
        } else {
          newData[existingIndex] = updatedConversation;
        }
      } else {
        // Ya no dependemos de isUpsert para insertar
        newData.unshift(updatedConversation);
      }

      const { normalizedData, conversationIndexMap } = normalizeConversations(newData);

      return {
        ...state,
        data: normalizedData,
        conversationIndexMap,
        externalConversation
      };
    }
    case SET_CONVERSATIONS_CURRENT_PAGE: {
      const currentPage = action.payload;
      return {
        ...state,
        pagination: {
          ...state.pagination,
          current_page: currentPage
        }
      };
    }
    case SET_ACTIVE_INBOX_FILTER:
      return {
        ...state,
        activeFilter: action.payload
      };
    case SET_ACTIVE_INBOX_SORT:
      return {
        ...state,
        activeSortConfig: action.payload
      };
    default:
      return state;
  }
}
